import type { SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import type { BulkSupportTicketAction } from "@redotech/effect-api-shared/models/shopper-ai/conversation/bulk-support-ticket-action";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { toast } from "@redotech/redo-web/alert";
import { useState } from "react";
import { catchApiErrors } from "../db/error-handling";

export function useBulkSupportTicketAction(): {
  pendingAction: BulkSupportTicketAction | null;
  startBulkSupportTicketAction: (input: {
    action: BulkSupportTicketAction;
    supportTicketIds: readonly SupportTicketId[];
  }) => Promise<void>;
} {
  const effectApi = useRequiredContext(EffectApiClientContext);
  const [pendingAction, setPendingAction] =
    useState<BulkSupportTicketAction | null>(null);

  const startBulkSupportTicketAction = useHandler(
    async ({
      action,
      supportTicketIds,
    }: {
      action: BulkSupportTicketAction;
      supportTicketIds: readonly SupportTicketId[];
    }) => {
      if (supportTicketIds.length === 0) {
        return;
      }

      setPendingAction(action);
      try {
        await effectApi.runPromise(
          catchApiErrors(
            effectApi
              .getClient()
              .supportTicket.startBulkSupportTicketAction({
                payload: { action, supportTicketIds: [...supportTicketIds] },
              }),
          ),
        );
      } catch {
        toast("Failed to start bulk action. Please try again.", {
          variant: "error",
        });
      } finally {
        setPendingAction(null);
      }
    },
  );

  return { pendingAction, startBulkSupportTicketAction };
}
